
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

type Theme = 'light' | 'dark';

export const useTheme = () => {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>('dark');
  const [isLoading, setIsLoading] = useState(true);

  // Apply theme to DOM with proper batching to prevent FOUC
  const applyTheme = useCallback((newTheme: Theme) => {
    // Use requestAnimationFrame with proper batching
    requestAnimationFrame(() => {
      // Batch DOM updates to prevent layout thrashing
      const root = document.documentElement;
      const currentTheme = root.getAttribute('data-theme');
      
      if (currentTheme !== newTheme) {
        // Apply theme immediately to prevent flash
        root.style.setProperty('--theme-transition', 'none');
        root.setAttribute('data-theme', newTheme);
        
        // Re-enable transitions after theme is applied
        requestAnimationFrame(() => {
          root.style.removeProperty('--theme-transition');
        });
      }
      
      setThemeState(newTheme);
    });
  }, []);

  // Save theme preference with error handling
  const saveThemePreference = useCallback(async (newTheme: Theme) => {
    if (user) {
      try {
        await supabase
          .from('profiles')
          .upsert({ 
            id: user.id, 
            theme: newTheme 
          });
      } catch (error) {
        console.warn('Failed to save theme to database:', error);
        // Fallback to localStorage without throwing
        try {
          localStorage.setItem('theme', newTheme);
        } catch (storageError) {
          console.warn('Failed to save theme to localStorage:', storageError);
        }
      }
    } else {
      try {
        localStorage.setItem('theme', newTheme);
      } catch (storageError) {
        console.warn('Failed to save theme to localStorage:', storageError);
      }
    }
  }, [user]);

  // Set theme function
  const setTheme = useCallback(async (newTheme: Theme) => {
    applyTheme(newTheme);
    await saveThemePreference(newTheme);
  }, [applyTheme, saveThemePreference]);

  // Toggle theme function
  const toggleTheme = useCallback(async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    await setTheme(newTheme);
  }, [theme, setTheme]);

  // Initialize theme with proper loading state management
  useEffect(() => {
    let isCancelled = false;

    const initializeTheme = async () => {
      // Prevent FOUC by setting initial theme immediately
      let savedTheme: Theme = 'dark';
      
      // Try to get theme from localStorage first (fastest)
      try {
        const localTheme = localStorage.getItem('theme') as Theme;
        if (localTheme && (localTheme === 'light' || localTheme === 'dark')) {
          savedTheme = localTheme;
          // Apply immediately to prevent flash
          if (!isCancelled) {
            applyTheme(savedTheme);
          }
        }
      } catch (error) {
        console.warn('Could not access localStorage for theme:', error);
      }

      // Then try to get from database if user is logged in
      if (user) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('theme')
            .eq('id', user.id)
            .single();
          
          if (!error && data?.theme && (data.theme === 'light' || data.theme === 'dark')) {
            savedTheme = data.theme as Theme;
          }
        } catch (error) {
          console.warn('Could not fetch theme from database:', error);
        }
      }
      
      if (!isCancelled) {
        applyTheme(savedTheme);
        setIsLoading(false);
      }
    };

    initializeTheme();

    return () => {
      isCancelled = true;
    };
  }, [user, applyTheme]);

  return {
    theme,
    setTheme,
    toggleTheme,
    isLoading
  };
};
